import { ApplicationRef, Injectable, inject, signal } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { Subscription, first, timer } from 'rxjs';

export interface UpdateCheckResult {
    time: Date;
    err?: any;
}

@Injectable({ providedIn: 'root' })
export class UpdateProvider {
    checkInterval = 3600 * 1000;
    checkInProgress = false;
    isSupported = true;

    available = signal<VersionReadyEvent | undefined>(undefined);
    activated = signal<any | undefined>(undefined);
    checkResult = signal<UpdateCheckResult | undefined>(undefined);
    lastSuccessfulCheck = signal<Date | undefined>(undefined);
    private updateCheckSub: Subscription | undefined;
    private readonly swUpdate = inject(SwUpdate);

    constructor() {
        const appRef = inject(ApplicationRef);
        this.swUpdate.versionUpdates?.subscribe({
            next: (event) => {
                if (event.type === 'VERSION_READY') {
                    this.available.set(event);
                }
            },
        });

        appRef.isStable
            .pipe(first((v) => v))
            .toPromise()
            .then(this.checkForUpdate, (err) => console.error(err));
    }

    checkForUpdate = async () => {
        if (this.checkInProgress) {
            return;
        }

        if (this.updateCheckSub) {
            this.updateCheckSub.unsubscribe();
            this.updateCheckSub = undefined;
        }
        this.checkInProgress = true;
        try {
            await this.swUpdate.checkForUpdate();
            const time = new Date();
            this.lastSuccessfulCheck.set(time);
            this.checkResult.set({ time });
            this.isSupported = true;
        } catch (err: any) {
            this.isSupported = !err || err.message !== 'Service workers are disabled or not supported by this browser';
            console.warn(err);
            this.checkResult.set({ time: new Date(), err });
        }
        this.checkInProgress = false;
        this.updateCheckSub = timer(this.checkInterval).subscribe({
            next: this.checkForUpdate,
        });
    };

    async restartToApply() {
        document.location.reload();
    }
}
